// Reactive core — browser component factory. Turns an isomorphic ComponentDef
// (src/core/ssr.ts) into a custom element that hydrates the server's Declarative
// Shadow DOM. This is the browser realization of the PRD's ReactiveElement:
// `define(Foo)` ⇒ `defineComponent(fooDef)`.

import { hydrate, render, makeScope, type Scope } from "./dom";
import type { ComponentDef } from "../core/ssr";

function readProps<P>(host: HTMLElement): P {
  const script = host.querySelector(":scope > script[data-hc-props]");
  if (!script?.textContent) return {} as P;
  try {
    return JSON.parse(script.textContent) as P;
  } catch {
    return {} as P;
  }
}

function applyStyles(root: ShadowRoot, css: string): void {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  } catch {
    const style = document.createElement("style");
    style.textContent = css;
    root.appendChild(style); // appended last → does not shift hydration node paths
  }
}

export function defineComponent<P, S>(def: ComponentDef<P, S>): void {
  if (customElements.get(def.tag)) return;

  class ReactiveElement extends HTMLElement {
    private scope: Scope | null = null;

    connectedCallback(): void {
      if (this.scope) return;
      this.scope = makeScope();
      try {
        const props = readProps<P>(this);
        const state = def.create(props);
        const tpl = def.view(state);
        const existing = this.shadowRoot;

        if (existing) {
          // Adopt the server-rendered shadow root. Move styles to an adopted
          // stylesheet and drop the server <style> so the remaining children
          // line up with the client template's node paths.
          if (def.styles) applyStyles(existing, def.styles);
          existing.querySelector("style")?.remove();
          hydrate(tpl, existing, this.scope);
        } else {
          const root = this.attachShadow({ mode: "open" });
          if (def.styles) applyStyles(root, def.styles);
          render(tpl, root, this.scope);
        }

        def.setup?.(state, this);
      } catch (err) {
        // A hydration failure must not blank the server content for other
        // components; log and leave the SSR markup in place.
        console.error(`[sc] hydration failed for <${def.tag}>`, err);
      }
    }

    disconnectedCallback(): void {
      this.scope?.dispose();
      this.scope = null;
    }
  }

  customElements.define(def.tag, ReactiveElement);
}
